import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ClearIcon from '@mui/icons-material/Clear';
import { makeStyles } from 'tss-react/mui';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { useSessionState } from '../../context/session/session';
import { getAnonymousToken } from './SaveFilterButton';
import NotificationConfigDialog from './NotificationConfigDialog';

const GET_SAVED_FILTERS = gql`
  query SavedFilters($anonymousToken: String) {
    savedFilters(anonymousToken: $anonymousToken) {
      id
      name
      filterData
      filterType
      isDefault
      notificationPreference {
        id
        notifyActors
        notifyEvents
        notifyArticles
        notifyRecipes
        channelEmail
        channelPush
        frequency
        isActive
      }
      createdAt
    }
  }
`;

const DELETE_SAVED_FILTER = gql`
  mutation DeleteSavedFilter($id: Int!) {
    deleteSavedFilter(id: $id)
  }
`;

const UNSET_DEFAULT_FILTER = gql`
  mutation UnsetDefaultFilter($id: Int!) {
    unsetDefaultFilter(id: $id)
  }
`;

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
  },
  listItem: {
    paddingRight: '96px',
  },
  listItemActive: {
    paddingRight: '96px',
    backgroundColor: '#e8f5e9',
    borderLeft: '3px solid #69b6a9',
  },
  filterName: {
    fontWeight: 500,
    fontSize: '0.9rem',
  },
  filterNameActive: {
    fontWeight: 700,
    fontSize: '0.9rem',
    color: '#2C367E',
  },
  chip: {
    fontSize: '0.7rem',
    height: '20px',
    marginLeft: '4px',
  },
  activeChip: {
    fontSize: '0.7rem',
    height: '20px',
    marginLeft: '4px',
    backgroundColor: '#69b6a9',
    color: 'white',
  },
  emptyText: {
    textAlign: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  collectionLabel: {
    textAlign: 'center',
    color: '#2C367E',
  },
  expansionPanel: {
    margin: 'inherit!important',
  },
  expansionPanelSummary: {
    padding: '0px 5px 0px 5px!important',
  },
  tutorialHighlight: {
    boxShadow: '0 0 0 3px #d96552, 0 0 16px rgba(217,101,82,0.5)',
    borderRadius: 6,
    animation: '$pulseTutorial 1.4s ease-in-out infinite',
  },
  '@keyframes pulseTutorial': {
    '0%, 100%': { boxShadow: '0 0 0 3px #d96552, 0 0 16px rgba(217,101,82,0.4)' },
    '50%': { boxShadow: '0 0 0 5px #d96552, 0 0 24px rgba(217,101,82,0.8)' },
  },
}));

interface SavedFilter {
  id: string | number;
  name: string;
  filterData?: any;
  filterType?: string;
  isDefault?: boolean;
  notificationPreference?: {
    id?: string | number;
    isActive?: boolean;
    [key: string]: any;
  };
}

interface SavedFiltersListProps {
  onApplyFilter?: (filterData: any, filter: SavedFilter) => void;
  refetchTrigger?: any;
  activeFilterId?: string | number | null;
  onClearActive?: () => void;
  activeStorageKey?: string;
  pageFilterType?: string;
}

function SavedFiltersList({
  onApplyFilter,
  refetchTrigger,
  activeFilterId,
  onClearActive,
  activeStorageKey,
  pageFilterType,
}: SavedFiltersListProps) {
  const { classes } = useStyles();
  const user = useSessionState();
  const { enqueueSnackbar } = useSnackbar();
  const [notifDialogFilter, setNotifDialogFilter] = useState<SavedFilter | null>(null);
  const hasAutoAppliedRef = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const [tutorialHighlight, setTutorialHighlight] = useState(false);

  useEffect(() => {
    const handler = (e: any) => {
      const isHighlight = e?.detail?.step === 2 || e?.detail?.step === 3;
      setTutorialHighlight(isHighlight);
      if (isHighlight) setExpanded(true);
    };
    window.addEventListener('ouaaa:tutorial-step', handler as EventListener);
    return () => window.removeEventListener('ouaaa:tutorial-step', handler as EventListener);
  }, []);

  const anonymousToken = user ? null : (typeof window !== 'undefined' ? getAnonymousToken() : null);

  const { data, loading, refetch } = useQuery(GET_SAVED_FILTERS, {
    variables: { anonymousToken },
    fetchPolicy: 'network-only',
  });

  const [deleteSavedFilter] = useMutation(DELETE_SAVED_FILTER);
  const [unsetDefaultFilter] = useMutation(UNSET_DEFAULT_FILTER);

  useEffect(() => {
    if (refetchTrigger) {
      refetch();
    }
  }, [refetchTrigger, refetch]);

  const handleDeactivate = useCallback(async (filter: SavedFilter) => {
    try {
      if (filter?.isDefault) {
        await unsetDefaultFilter({ variables: { id: parseInt(String(filter.id)) } });
      }
      if (onClearActive) onClearActive();
      await refetch();
      enqueueSnackbar(
        filter?.isDefault ? 'Filtre désactivé et retiré des filtres par défaut' : 'Filtre désactivé',
        { variant: 'info', preventDuplicate: true },
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erreur lors de la désactivation du filtre', { variant: 'error' });
    }
  }, [unsetDefaultFilter, onClearActive, refetch, enqueueSnackbar]);

  const handleApply = useCallback((filter: SavedFilter) => {
    if (activeFilterId && String(activeFilterId) === String(filter.id)) {
      handleDeactivate(filter);
      return;
    }
    if (onApplyFilter) {
      onApplyFilter(filter.filterData, filter);
    }
    enqueueSnackbar(`Filtre "${filter.name}" appliqué`, { variant: 'info', preventDuplicate: true });
  }, [onApplyFilter, enqueueSnackbar, activeFilterId, handleDeactivate]);

  const handleDelete = useCallback(async (filter: SavedFilter) => {
    try {
      await deleteSavedFilter({ variables: { id: parseInt(String(filter.id)) } });

      if (!user) {
        const localFilters = JSON.parse(localStorage.getItem('ouaaa_saved_filters') || '[]');
        const updated = localFilters.filter((f: any) => f.id !== filter.id);
        localStorage.setItem('ouaaa_saved_filters', JSON.stringify(updated));
      }

      if (activeFilterId && String(activeFilterId) === String(filter.id) && onClearActive) {
        onClearActive();
      }

      enqueueSnackbar('Filtre supprimé', { variant: 'success' });
      refetch();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erreur lors de la suppression', { variant: 'error' });
    }
  }, [deleteSavedFilter, user, refetch, enqueueSnackbar, activeFilterId, onClearActive]);

  const filters: SavedFilter[] = data?.savedFilters || [];

  const matchesPageType = useCallback((filter: SavedFilter) => {
    if (!filter) return false;
    return filter.filterType === 'both' || filter.filterType === pageFilterType;
  }, [pageFilterType]);

  const eligibleFilters = filters.filter(matchesPageType);

  useEffect(() => {
    if (loading || hasAutoAppliedRef.current || filters.length === 0 || !onApplyFilter) return;

    hasAutoAppliedRef.current = true;

    if (eligibleFilters.length === 0) return;

    let filterToApply: SavedFilter | null = null;
    if (typeof window !== 'undefined' && activeStorageKey) {
      const storedId = localStorage.getItem(activeStorageKey);
      if (storedId) {
        filterToApply = eligibleFilters.find((filter) => String(filter.id) === String(storedId)) || null;
      }
    }

    if (!filterToApply) {
      filterToApply = eligibleFilters.find((filter) => filter.isDefault) || null;
    }

    if (filterToApply) {
      onApplyFilter(filterToApply.filterData, filterToApply);
    }
  }, [loading, eligibleFilters, onApplyFilter, activeStorageKey]);

  useEffect(() => {
    if (activeFilterId || eligibleFilters.some((filter) => filter.isDefault)) {
      setExpanded(true);
    }
  }, [activeFilterId, eligibleFilters]);

  if (loading && eligibleFilters.length === 0) return null;

  return (
    <>
      <Accordion
        className={`${classes.expansionPanel} ${tutorialHighlight ? classes.tutorialHighlight : ''}`}
        expanded={expanded}
        onChange={(_event, isExpanded) => setExpanded(isExpanded)}
        data-tour="saved-filters"
      >
        <AccordionSummary className={classes.expansionPanelSummary} expandIcon={<ExpandMoreIcon />}>
          <BookmarkIcon style={{ color: '#2C367E', marginRight: 8 }} />
          <Typography className={classes.collectionLabel}>
            Filtres sauvegardés ({eligibleFilters.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails style={{ flexDirection: 'column', padding: 0 }}>
          {eligibleFilters.length === 0 ? (
            <Typography className={classes.emptyText}>
              Aucun filtre sauvegardé
            </Typography>
          ) : (
            <List dense>
              {eligibleFilters.map((filter) => {
                const hasActiveNotif = filter.notificationPreference?.isActive;
                const isActive = activeFilterId && String(activeFilterId) === String(filter.id);
                return (
                  <ListItem
                    key={filter.id}
                    className={isActive ? classes.listItemActive : classes.listItem}
                    button
                    onClick={() => handleApply(filter)}
                  >
                    <ListItemText
                      primary={
                        <span className={isActive ? classes.filterNameActive : classes.filterName}>
                          {filter.name}
                          {isActive && (
                            <Chip
                              label="Actif"
                              size="small"
                              className={classes.activeChip}
                            />
                          )}
                          {filter.isDefault && (
                            <Chip
                              label="Par défaut"
                              size="small"
                              className={classes.chip}
                              color="primary"
                            />
                          )}
                          {filter.filterType !== 'both' && (
                            <Chip
                              label={filter.filterType === 'actors' ? 'Acteurs' : 'Événements'}
                              size="small"
                              className={classes.chip}
                            />
                          )}
                        </span>
                      }
                    />
                    <ListItemSecondaryAction>
                      {isActive && (
                        <Tooltip title="Désactiver le filtre">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await handleDeactivate(filter);
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Configurer les notifications">
                        <IconButton
                          edge="end"
                          size="small"
                          data-tour="notification-bell"
                          onClick={(e) => {
                            e.stopPropagation();
                            setNotifDialogFilter(filter);
                          }}
                        >
                          {hasActiveNotif ? (
                            <NotificationsActiveIcon style={{ color: '#69b6a9' }} />
                          ) : (
                            <NotificationsIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(filter);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </AccordionDetails>
      </Accordion>

      {notifDialogFilter && (
        <NotificationConfigDialog
          open={!!notifDialogFilter}
          filter={notifDialogFilter}
          onClose={() => {
            setNotifDialogFilter(null);
            refetch();
          }}
        />
      )}
    </>
  );
}

export default SavedFiltersList;
